"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { waxClientService } from "@/lib/wax";
import { clientLogger } from "@/lib/client-logger";

interface EnhancedWaxCloudWalletSignupProps {
  userEmail: string;
  firstName?: string;
  lastName?: string;
  onSuccess: (accountData: {
    accountName: string;
    isTemp: boolean;
    email: string;
  }) => void;
  onError: (error: string) => void;
  onCancel: () => void;
}

export function EnhancedWaxCloudWalletSignup({ 
  userEmail, 
  firstName, 
  lastName, 
  onSuccess, 
  onError, 
  onCancel 
}: EnhancedWaxCloudWalletSignupProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'signup' | 'connecting' | 'completed'>('signup');
  const [tempAccountName, setTempAccountName] = useState<string>('');

  const handleWaxSignup = async () => {
    setIsLoading(true);
    setStep('signup');

    try {
      clientLogger.info("Starting enhanced WAX Cloud Wallet signup with Wharfkit", { email: userEmail });

      // Initialize Wharfkit if not already done
      await waxClientService.initialize();

      // Check if user is already logged in
      const isAutoLoginAvailable = await waxClientService.isAutoLoginAvailable();

      if (!isAutoLoginAvailable) {
        clientLogger.debug("Starting Cloud Wallet registration/login flow");
        setStep('connecting');
      }
      
      // Attempt login with Cloud Wallet using Wharfkit
      // This will handle both registration and login flows
      const loginResult = await waxClientService.loginWithCloudWallet({
        email: userEmail,
        firstName: firstName,
        lastName: lastName,
        autoClose: true, // Try to auto-close after registration
        returnUrl: window.location.origin + '/auth/signup/digital-wallet'
      });
      
      if (!loginResult.success || !loginResult.userAccount) {
        throw new Error(loginResult.error || 'WAX login failed after registration');
      }

      const { accountName, isTemp } = loginResult.userAccount;
      clientLogger.info("WAX login successful", { accountName, isTemp });

      // Store temp account info
      setTempAccountName(accountName);
      setStep('completed');

      // Check if the WAX account email matches the expected email
      // Note: We can't directly verify this due to WAX API limitations,
      // but we'll proceed with the assumption that the user followed instructions
      clientLogger.warn("Cannot verify WAX registration email matches expected email", { expectedEmail: userEmail });

      // Return account data (temp or permanent)
      onSuccess({
        accountName,
        isTemp: isTemp || false,
        email: userEmail // We assume they used the correct email
      });

    } catch (error) {
      clientLogger.error("Enhanced WAX signup error", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      const errorMessage = error instanceof Error ? error.message : 'WAX signup failed';
      onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getStepContent = () => {
    switch (step) {
      case 'signup':
        return {
          title: 'Create Your Digital Asset Wallet',
          description: `We will connect you to WAX Cloud Wallet with your information pre-configured. You can sign up with Google, Facebook, or other social accounts.`,
          buttonText: isLoading ? 'Connecting to WAX...' : 'Create WAX Wallet',
          showCancel: true
        };
      case 'connecting':
        return {
          title: 'Complete Your Registration',
          description: `Please complete your WAX Cloud Wallet registration in the popup window using your email: ${userEmail}. Choose social login (Google, Facebook, etc.) for the easiest setup. Once done, we will automatically connect your account.`,
          buttonText: 'Connecting...',
          showCancel: false
        };
      case 'completed':
        return {
          title: 'Account Connected Successfully!',
          description: `Your WAX Cloud Wallet "${tempAccountName}" is now connected. You can continue with your scavenger hunt signup.`,
          buttonText: 'Account Connected!',
          showCancel: false
        };
    }
  };

  const content = getStepContent();

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg border">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
          {step === 'signup' && (
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          )}
          {step === 'connecting' && (
            <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {step === 'completed' && (
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {content.title}
        </h3>
        
        <p className="text-gray-600 text-sm leading-relaxed">
          {content.description}
        </p>

        {step === 'signup' && (
          <div className="mt-3 space-y-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-700 mb-2">
                <strong>⚠️ Important:</strong> You MUST use this exact email for your WAX Cloud Wallet:
              </p>
              <div className="flex items-center space-x-2 bg-white rounded px-2 py-1 border border-red-300">
                <span className="text-xs font-mono text-gray-800 flex-1 font-bold">{userEmail}</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(userEmail);
                  }}
                  className="text-red-600 hover:text-red-800 text-xs"
                  title="Copy email"
                >
                  📋
                </button>
              </div>
              <p className="text-xs text-red-600 mt-2">
                Using a different email will prevent your accounts from being properly linked.
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700">
                <strong>💡 Enhanced Experience:</strong> Wharfkit will handle the registration flow and automatically connect your account when complete.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Button
          onClick={handleWaxSignup}
          disabled={isLoading || step === 'completed'}
          className="w-full"
          variant={step === 'completed' ? 'default' : 'default'}
        >
          {content.buttonText}
        </Button>

        {content.showCancel && (
          <Button
            onClick={onCancel}
            disabled={isLoading}
            variant="outline"
            className="w-full"
          >
            Cancel
          </Button>
        )}
      </div>

      {step === 'connecting' && (
        <div className="mt-4 space-y-3">
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700 mb-2">
              <strong>Registration Steps:</strong>
            </p>
            <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
              <li>Use email: <span className="font-mono bg-white px-1 rounded font-bold">{userEmail}</span></li>
              <li>Choose social login (Google, Facebook, etc.)</li>
              <li>Complete the registration process</li>
              <li>Manually close the popup window when finished</li>
            </ol>
          </div>
          <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-xs text-yellow-700">
              <strong>Tip:</strong> If the popup is blocked, click the &quot;Create WAX Wallet&quot; button again to retry.
            </p>
          </div>
        </div>
      )}

      {step === 'completed' && (
        <div className="mt-4 p-3 bg-green-50 rounded-lg">
          <p className="text-xs text-green-700">
            <strong>Success!</strong> Your WAX Cloud Wallet is connected. The blockchain account will be activated when you complete the signup process.
          </p>
        </div>
      )}
    </div>
  );
}
